import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  WIDERRUF_PFADE,
  angabenZeilen,
  zeitpunktText,
  type ErklaerungBestaetigung,
} from "@/lib/vertragErklaerung";

/**
 * Bestätigungsseite nach „Widerruf bestätigen“: Inhalt der Erklärung mit Datum und Uhrzeit des Eingangs, zum Speichern
 * oder Drucken (§ 356a BGB). Die Angaben kommen nur über den Verlauf des Browsers; wer die Adresse direkt öffnet, sieht
 * einen Hinweis und den Weg zum Formular.
 */
export function ErklaerungsBestaetigungAnzeige() {
  const { state } = useLocation();
  const b = state as ErklaerungBestaetigung | null;

  if (!b?.eingegangen_am || b.eingabe?.art !== "widerruf") {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold text-foreground">Widerruf</h1>
        <p className="text-muted-foreground">
          Diese Seite zeigt die Bestätigung direkt nach dem Absenden. Die Bestätigung mit Datum und Uhrzeit hast du außerdem
          per E-Mail bekommen.
        </p>
        <Link to={WIDERRUF_PFADE.formular} className="font-semibold text-[#227277] underline underline-offset-2">
          Zu „Vertrag widerrufen“
        </Link>
      </div>
    );
  }

  const zeilen: [string, string][] = [
    ...angabenZeilen(b.eingabe),
    ["Eingegangen am", zeitpunktText(b.eingegangen_am)],
    ...(b.referenz ? ([["Vorgangsnummer", b.referenz]] as [string, string][]) : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-1 h-7 w-7 shrink-0 text-[#227277]" aria-hidden="true" />
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dein Widerruf ist eingegangen</h1>
          <p className="mt-2 text-muted-foreground">
            Eingegangen am <strong className="text-foreground">{zeitpunktText(b.eingegangen_am)}</strong>.
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <dl className="divide-y divide-border">
          {zeilen.map(([k, v]) => (
            <div key={k} className="grid gap-1 px-4 py-3 sm:grid-cols-[220px_1fr] sm:gap-4">
              <dt className="text-sm text-muted-foreground">{k}</dt>
              <dd className="whitespace-pre-line break-words text-sm text-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      </div>

      {b.mail_gesendet ? (
        <p className="text-sm text-muted-foreground">
          Diese Bestätigung haben wir dir auch per E-Mail an <strong className="text-foreground">{b.eingabe.email}</strong>{" "}
          geschickt. Schau bitte auch im Spam-Ordner nach.
        </p>
      ) : (
        <p className="rounded-lg border border-[#F5A623]/50 bg-[#FFF6E6] px-4 py-3 text-sm text-[#1D2934]">
          Dein Widerruf ist bei uns eingegangen, aber die Bestätigung per E-Mail konnte gerade nicht verschickt werden.
          Bitte speichere oder drucke diese Seite als Nachweis.
        </p>
      )}

      <div className="flex flex-wrap gap-3 print:hidden">
        <Button
          type="button"
          variant="outline"
          className="min-h-[44px] rounded-full border-[#227277] text-[#227277]"
          onClick={() => window.print()}
        >
          <Printer className="mr-2 h-4 w-4" />
          Seite drucken oder als PDF speichern
        </Button>
        <Button asChild className="min-h-[44px] rounded-full bg-[#227277] text-white hover:bg-[#1D2934]">
          <Link to="/">Zur Startseite</Link>
        </Button>
      </div>
    </div>
  );
}
